import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const normalize = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('pt-BR');

/**
 * Styled, searchable select. The input filters the list, but only one of the
 * supplied options is emitted as a valid value.
 */
export default function FilterableSelect({
  id,
  name,
  value = '',
  options = [],
  onValueChange,
  placeholder,
  required = false,
  autoFocus = false,
  className = '',
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const syncedValueRef = useRef(value);
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);

  const exactOption = useMemo(() => (
    options.find((option) => normalize(option) === normalize(query)) || ''
  ), [options, query]);
  const filteredOptions = useMemo(() => {
    const search = normalize(query);
    return options.filter((option) => normalize(option).includes(search));
  }, [options, query]);

  useEffect(() => {
    // Adopt values set by a parent (such as the pre-filled exam on a detail page)
    // without clearing the query while the visitor is actively searching.
    if (value !== syncedValueRef.current) {
      syncedValueRef.current = value;
      setQuery(value || '');
    }
  }, [value]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.setCustomValidity(required && !exactOption ? 'Selecione uma opção da lista.' : '');
  }, [exactOption, required]);

  useEffect(() => {
    const closeOnOutsidePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, []);

  const updateQuery = (nextQuery) => {
    setQuery(nextQuery);
    const matchingOption = options.find((option) => normalize(option) === normalize(nextQuery)) || '';
    syncedValueRef.current = matchingOption;
    onValueChange(matchingOption);
  };

  const chooseOption = (option) => {
    setQuery(option);
    syncedValueRef.current = option;
    onValueChange(option);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={`${id}-options`}
        aria-expanded={open}
        aria-required={required || undefined}
        required={required}
        autoFocus={autoFocus}
        autoComplete="off"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          updateQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
          if (event.key === 'ArrowDown') setOpen(true);
        }}
        placeholder={placeholder}
        className={className}
      />
      <button
        type="button"
        onClick={() => {
          setOpen((current) => !current);
          inputRef.current?.focus();
        }}
        aria-label={open ? 'Fechar opções' : 'Mostrar opções'}
        className="absolute right-0 top-0 flex h-full w-11 items-center justify-center text-cdi-blue focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cdi-yellow/70"
      >
        <ChevronDown size={17} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul
          id={`${id}-options`}
          role="listbox"
          className="absolute z-[70] mt-1 max-h-56 w-full overflow-y-auto border-2 border-cdi-blue bg-white py-1 shadow-[4px_4px_0_0_#FFBE0B]"
        >
          {filteredOptions.length > 0 ? filteredOptions.map((option) => (
            <li key={option} role="option" aria-selected={option === value}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseOption(option)}
                className="w-full px-4 py-3 text-left text-sm font-bold text-cdi-carbon transition-colors hover:bg-cdi-blue hover:text-white focus-visible:bg-cdi-blue focus-visible:text-white focus-visible:outline-none"
              >
                {option}
              </button>
            </li>
          )) : (
            <li className="px-4 py-3 text-sm font-medium text-cdi-carbon/55">Nenhuma opção encontrada.</li>
          )}
        </ul>
      )}
    </div>
  );
}
